import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import CheckoutButton from "@/components/CheckoutButton";
import PromoCodeInput from "@/components/PromoCodeInput";

export default function Pricing() {
    const { t } = useTranslation();
    const [plans, setPlans] = useState([]);
    const [yearly, setYearly] = useState(false);
    const { user } = useAuth();
    const [promo, setPromo] = useState(() => user?.signup_promo_code || "");

    useEffect(() => {
        api.get("/public/plans").then((r) => setPlans(r.data));
    }, []);

    const plan = plans[0];

    return (
        <section className="bg-cream min-h-[70vh]">
            <div className="mx-auto max-w-5xl px-6 lg:px-10 py-16 lg:py-24">
                <div className="text-center mb-14">
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                        {t("pricing.eyebrow")}
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl text-navy-700 leading-[1.05]">
                        {t("pricing.title")}
                    </h1>
                    <p className="text-navy-500 mt-4 max-w-lg mx-auto">
                        {t("pricing.subtitle")}
                    </p>

                    <div className="mt-8 inline-flex items-center rounded-full bg-white p-1 shadow-soft">
                        <button
                            data-testid="billing-monthly"
                            onClick={() => setYearly(false)}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                                !yearly
                                    ? "bg-navy-700 text-white"
                                    : "text-navy-500 hover:text-navy-700"
                            }`}
                        >
                            {t("pricing.toggle_monthly")}
                        </button>
                        <button
                            data-testid="billing-yearly"
                            onClick={() => setYearly(true)}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                                yearly
                                    ? "bg-navy-700 text-white"
                                    : "text-navy-500 hover:text-navy-700"
                            }`}
                        >
                            {t("pricing.toggle_yearly")} <span className="text-gold-500 ml-1">{t("pricing.toggle_yearly_off")}</span>
                        </button>
                    </div>
                </div>

                {plan && (
                    <div
                        data-testid={`pricing-${plan.id}`}
                        className="rounded-3xl bg-navy-700 text-white shadow-lift p-10 lg:p-14 grid md:grid-cols-[1fr_1px_1fr] gap-10 items-center"
                    >
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-3">
                                {t("pricing.plan_eyebrow")}
                            </div>
                            <div className="font-display text-7xl mb-2">
                                ${yearly ? Math.round(plan.price_yearly / 12) : plan.price_monthly}
                                <span className="text-lg font-sans text-navy-50/60">
                                    {" "}{t("pricing.per_month")}
                                </span>
                            </div>
                            <div className="text-navy-50/70 text-lg mb-6">
                                {plan.tagline}
                            </div>
                            {user ? (
                                <div className="space-y-3">
                                    <PromoCodeInput
                                        context="signup"
                                        value={promo}
                                        onApplied={setPromo}
                                        testidPrefix="pricing-promo"
                                        className="max-w-xs [&_input]:text-red-600"
                                    />
                                    <CheckoutButton
                                        kind="membership"
                                        interval={yearly ? "yearly" : "monthly"}
                                        customerEmail={user.email}
                                        userId={user.id}
                                        promoCode={promo || undefined}
                                        testid="pricing-subscribe-btn"
                                        className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 h-12 px-8 font-semibold border-0"
                                    >
                                        {plan.cta}
                                    </CheckoutButton>
                                </div>
                            ) : (
                                <Link to="/signup">
                                    <Button
                                        data-testid="pricing-signup-btn"
                                        className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 h-12 px-8 font-semibold"
                                    >
                                        {plan.cta}
                                    </Button>
                                </Link>
                            )}
                            <div className="text-xs text-navy-50/50 mt-4">
                                {t("pricing.cancel_note")}
                            </div>
                        </div>
                        <div className="hidden md:block h-full w-px bg-white/10" />
                        <ul className="space-y-3 text-sm text-navy-50/90">
                            {plan.features.map((f) => (
                                <li key={f} className="flex gap-3">
                                    <Check size={18} className="text-gold-500 mt-0.5" />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </section>
    );
}
